import React, { useEffect, useState } from "react";
import { Checkbox, Input, Modal, message } from "antd";
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleFilled,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

import { controller as treatmentsController } from "../controller";
import { controller as rootController } from "../../../controller";
import { MISSING_INFO_TAG_NAME, REQUEST_TAGS_CLEARED_BY_DONE, requestTypeMeta } from "../../InsuranceRequests/requestTypes";
import { catalogueGroups, keysForMissingCritical, orderKeys } from "../../InsuranceRequests/infoAsk";
import { missingCriticalFields } from "../lib/policyCompleteness";

// Fallback-only (no-policy) mapping: the request tags a success clears. One
// vocabulary for all three request types (breakdown / estimate / claim follow-up).
const REQUEST_TAGS_FOR = REQUEST_TAGS_CLEARED_BY_DONE;

/**
 * FTC "mark outcome" control for the breakdown / estimate modals — PER POLICY.
 *
 * The button on/off state reflects the SELECTED policy (`policySuccess` /
 * `policyNeedsAttention`), not the patient's rollup tags — so a patient with one
 * verified policy and one flagged policy shows the right buttons per selection.
 *
 * Writes are policy-scoped too:
 *  - "Mark Verified / Estimate" → fulfillVerificationRequest (fulfills the FTC
 *    request + meters + sets the policy's success state + maintains the tag).
 *  - "Details Needed by Smilepass" / its toggle-off / success toggle-off → the per-policy
 *    outcome endpoint (updatePolicyAttentionNote), which sets/clears the policy
 *    timestamps and maintains the patient rollup tags policy-aware. Marking
 *    Details Needed by Smilepass also clears the tab's success state (mutual exclusivity).
 *  - "Unable to obtain breakdown" (breakdowns only) → fulfillVerificationRequest
 *    with result "unavailable": closes the policy's open breakdown request with
 *    the FTC's note (required — the office reads it), unbilled, and emails the
 *    office. An action, not a toggle: it leaves nothing on the policy to undo.
 *
 * Lives on the Breakdown tab only since outcomes v2: an estimate or a claim is
 * answered item by item with Record result (SubmissionResultForm).
 *
 * `onNoteSaved(policyId, patch)` hands the parent an optimistic per-policy patch
 * (an object of the changed `*_at` / note fields) so the buttons + note header +
 * board chips all re-derive from the updated selected policy.
 */
export default function OutcomeTagControl({
  patient, successTagName, onNoteSaved, policyId, requestType, policyNote,
  policySuccess, policyNeedsAttention,
  // The selected policy (its blank critical fields start the checklist
  // ticked), and the agent's suggestion when it failed on policy data: the
  // wording to send on and the catalogue keys it points at.
  policy, suggestedNote, suggestedFields,
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [outcome, setOutcome] = useState(null); // "success" | "attention" | "unavailable"
  const [busy, setBusy] = useState(false);
  // "Details Needed by Smilepass": what the office must provide, as catalogue
  // keys (InsuranceRequests/infoAsk), plus "Other" in the FTC's own words.
  const [askFields, setAskFields] = useState([]);
  const [otherOn, setOtherOn] = useState(false);
  const [otherText, setOtherText] = useState("");
  // Kept only for the defensive no-policy fallback path (the modal always has a
  // policy in practice); the per-policy button state comes from props below.
  const [localTags, setLocalTags] = useState(() =>
    Array.isArray(patient && patient.tags) ? patient.tags : [],
  );
  useEffect(() => {
    setLocalTags(Array.isArray(patient && patient.tags) ? patient.tags : []);
  }, [patient && patient.id]);

  // The per-policy success timestamp this tab owns, from the one request-type
  // vocabulary — never a two-way ternary whose `else` means "breakdown".
  const typeMeta = requestTypeMeta(requestType);
  const successField = typeMeta.successField;
  const patch = (fields) => {
    if (typeof onNoteSaved === "function") onNoteSaved(policyId, fields);
  };

  function start(which) {
    if (!patient || !patient.id) {
      message.error("No patient is selected.");
      return;
    }
    if (which === "unavailable" && !policyId) {
      message.error("Pick the policy first.");
      return;
    }
    setOutcome(which);
    // The reason is new news for the office — never an older note carried over.
    // Details Needed starts from the policy's note, else the agent's suggested
    // wording; its checklist from the blank critical fields and what the
    // agent's failure points at.
    setNote(which === "unavailable" ? "" : (policyNote || (which === "attention" && suggestedNote) || ""));
    if (which === "attention") {
      const fromPolicy = policy ? keysForMissingCritical(missingCriticalFields(policy)) : [];
      setAskFields(orderKeys(fromPolicy.concat(suggestedFields || [])));
      setOtherOn(false);
      setOtherText("");
    }
    setOpen(true);
  }

  async function apply() {
    setBusy(true);
    try {
      const nowIso = new Date().toISOString();
      const noteAt = note ? nowIso : null;
      if (policyId && outcome === "unavailable") {
        const why = note.trim();
        const res = await treatmentsController.fulfillVerificationRequest(
          patient.id, policyId, "breakdown", why, "unavailable",
        );
        if (!(res && res.status >= 200 && res.status < 300)) {
          message.error((res && res.json && res.json.detail) || "Could not close the request.");
          return;
        }
        if (res.json && Array.isArray(res.json.tags)) {
          patient.tags = res.json.tags;
          setLocalTags(res.json.tags);
        }
        message.success("Closed as unable to obtain breakdown — the office has been told.");
        setOpen(false);
        patch({ needs_attention_at: null, attention_note: why, attention_note_set_at: nowIso });
        return;
      }
      if (policyId) {
        // "Mark Verified / Estimate" → fulfill (closes the FTC row + meters).
        if (outcome === "success" && requestType) {
          const res = await treatmentsController.fulfillVerificationRequest(patient.id, policyId, requestType, note);
          if (!(res && res.status >= 200 && res.status < 300)) {
            message.error((res && res.json && res.json.detail) || "Could not mark verified.");
            return;
          }
          if (res.json && Array.isArray(res.json.tags)) {
            patient.tags = res.json.tags;
            setLocalTags(res.json.tags);
          }
          message.success(successLabel + " applied.");
          setOpen(false);
          patch({ [successField]: nowIso, needs_attention_at: null, attention_note: note || null, attention_note_set_at: noteAt });
          return;
        }
        // "Details Needed by Smilepass" → set the flag + note, clear this tab's success.
        if (outcome === "attention") {
          // Explicit equality per type: with `!== "estimate"` a third type
          // silently cleared the BREAKDOWN timestamp on a policy it never touched.
          const askOn = askFields.length > 0 || (otherOn && otherText.trim());
          const res = await rootController.updatePolicyAttentionNote(policyId, note, {
            needsAttention: true,
            clearVerified: requestType === "breakdown",
            clearEstimate: requestType === "estimate",
            clearClaim: requestType === "claim_followup",
            // The checklist; a message alone is the older note-only flag.
            ...(askOn ? { ask: { fields: askFields, other_text: otherOn ? otherText.trim() : "" } } : {}),
          });
          if (!(res && res.status >= 200 && res.status < 300)) {
            message.error((res && res.json && res.json.detail) || "Could not mark Details Needed by Smilepass.");
            return;
          }
          message.success("Marked Details Needed by Smilepass — the office has been told.");
          setOpen(false);
          patch({
            needs_attention_at: nowIso, [successField]: null, attention_note: note || null, attention_note_set_at: noteAt,
            ...(res.json && res.json.info_ask !== undefined ? { info_ask: res.json.info_ask } : {}),
          });
          return;
        }
      }

      // --- No-policy fallback (defensive): patient-level tag flip. ---
      const tagsRes = await treatmentsController.getAllTags();
      const allTags = (tagsRes && tagsRes.json) || [];
      const addName = outcome === "success" ? successTagName : MISSING_INFO_TAG_NAME;
      const requestTags = REQUEST_TAGS_FOR[successTagName] || [];
      const removeNames = outcome === "success" ? requestTags : requestTags.filter((n) => n !== MISSING_INFO_TAG_NAME);
      const addTag = allTags.find((t) => t.name === addName);
      if (!addTag) {
        message.error('The "' + addName + '" tag is not set up for this office.');
        return;
      }
      const removeSet = {};
      removeNames.forEach((n) => { removeSet[n] = true; });
      const keptIds = allTags.filter((t) => localTags.includes(t.name) && !removeSet[t.name]).map((t) => t.id);
      const nextIds = keptIds.indexOf(addTag.id) === -1 ? keptIds.concat([addTag.id]) : keptIds;
      await treatmentsController.setTagsForPatient(patient.id, nextIds, note);
      patient.manual_attention_note = note;
      const nextNames = allTags.filter((t) => nextIds.indexOf(t.id) !== -1).map((t) => t.name);
      patient.tags = nextNames;
      setLocalTags(nextNames);
      message.success(outcome === "success" ? addName + " applied." : "Marked Details Needed by Smilepass.");
      setOpen(false);
    } catch (e) {
      console.error(e);
      message.error("Could not update the outcome.");
    } finally {
      setBusy(false);
    }
  }

  // Toggle OFF an already-applied outcome (no dialog).
  async function remove(which) {
    if (!patient || !patient.id) {
      message.error("No patient is selected.");
      return;
    }
    setBusy(true);
    try {
      if (policyId) {
        if (which === "success") {
          // Un-mark this tab's success (clears the policy state + drops the
          // rollup tag when no other policy still has it). Leaves the note.
          const res = await rootController.updatePolicyAttentionNote(policyId, undefined, {
            clearVerified: requestType === "breakdown",
            clearEstimate: requestType === "estimate",
            clearClaim: requestType === "claim_followup",
          });
          if (!(res && res.status >= 200 && res.status < 300)) {
            message.error((res && res.json && res.json.detail) || "Could not update.");
            return;
          }
          message.success(successLabel + " removed.");
          patch({ [successField]: null });
        } else {
          const res = await rootController.updatePolicyAttentionNote(policyId, "", { needsAttention: false });
          if (!(res && res.status >= 200 && res.status < 300)) {
            message.error((res && res.json && res.json.detail) || "Could not update.");
            return;
          }
          message.success("Details Needed by Smilepass removed.");
          patch({
            needs_attention_at: null, attention_note: null, attention_note_set_at: null,
            ...(res.json && res.json.info_ask !== undefined ? { info_ask: res.json.info_ask } : {}),
          });
        }
        return;
      }

      // --- No-policy fallback. ---
      const removeName = which === "success" ? successTagName : MISSING_INFO_TAG_NAME;
      const tagsRes = await treatmentsController.getAllTags();
      const allTags = (tagsRes && tagsRes.json) || [];
      const nextIds = allTags
        .filter((t) => localTags.includes(t.name) && t.name !== removeName)
        .map((t) => t.id);
      const noteArg = which === "attention" ? "" : undefined;
      await treatmentsController.setTagsForPatient(patient.id, nextIds, noteArg);
      if (which === "attention") patient.manual_attention_note = "";
      const nextNames = allTags.filter((t) => nextIds.indexOf(t.id) !== -1).map((t) => t.name);
      patient.tags = nextNames;
      setLocalTags(nextNames);
      message.success(removeName + " removed.");
    } catch (e) {
      console.error(e);
      message.error("Could not update the outcome.");
    } finally {
      setBusy(false);
    }
  }

  const successLabel = typeMeta.successLabel;

  // Button on/off reflects the SELECTED POLICY's state (falls back to the patient
  // tags only when there's no policy — the defensive path).
  const isSuccess = policyId ? !!policySuccess : localTags.indexOf(successTagName) !== -1;
  const isAttention = policyId ? !!policyNeedsAttention : localTags.indexOf(MISSING_INFO_TAG_NAME) !== -1;

  const baseBtn = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    height: 32,
    padding: "0 14px",
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    transition: "all 0.18s ease",
  };
  const successStyle = isSuccess
    ? { ...baseBtn, background: "#52c41a", color: "#fff", border: "1px solid #389e0d", boxShadow: "0 2px 8px rgba(82,196,26,0.35)" }
    : { ...baseBtn, background: "#f6ffed", color: "#389e0d", border: "1px solid #b7eb8f" };
  const attentionStyle = isAttention
    ? { ...baseBtn, background: "#faad14", color: "#613400", border: "1px solid #d48806", boxShadow: "0 2px 8px rgba(250,173,20,0.45)" }
    : { ...baseBtn, background: "#fffbe6", color: "#d48806", border: "1px solid #ffe58f" };
  // STATUS_PALETTE.cancelled — the office's "Unable to obtain breakdown" filter colour.
  const unavailableStyle = { ...baseBtn, background: "#fafafa", color: "#8c8c8c", border: "1px solid #d9d9d9" };
  const isUnavailable = outcome === "unavailable";
  // Details Needed can go with something ticked, Other said, or a message;
  // "Other" ticked and left blank says nothing.
  const askOk = (askFields.length > 0 || (otherOn && !!otherText.trim()) || !!note.trim()) && !(otherOn && !otherText.trim());
  const groups = catalogueGroups();
  const toggleGroup = (group, values) => {
    const inGroup = group.items.map((it) => it.key);
    setAskFields(orderKeys(askFields.filter((k) => inGroup.indexOf(k) === -1).concat(values)));
  };

  return (
    <>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          style={successStyle}
          disabled={busy}
          title={isSuccess ? `${successLabel} is set — click to remove` : successLabel}
          onClick={() => (isSuccess ? remove("success") : start("success"))}
        >
          {isSuccess ? <CheckCircleFilled /> : <CheckCircleOutlined />}
          {successLabel}
        </button>
        <button
          type="button"
          style={attentionStyle}
          disabled={busy}
          title={isAttention ? "Details Needed by Smilepass is set — click to remove" : "Tell the office what's missing"}
          onClick={() => (isAttention ? remove("attention") : start("attention"))}
        >
          {isAttention ? <ExclamationCircleFilled /> : <ExclamationCircleOutlined />}
          Details Needed by Smilepass
        </button>
        {requestType === "breakdown" ? (
          <button
            type="button"
            style={unavailableStyle}
            disabled={busy}
            title="Close the request: our team could not obtain this breakdown"
            onClick={() => start("unavailable")}
          >
            <CloseCircleOutlined />
            Unable to obtain breakdown
          </button>
        ) : null}
      </div>
      <Modal
        open={open}
        title={outcome === "success" ? successLabel : isUnavailable ? "Unable to obtain breakdown" : "Details Needed by Smilepass"}
        okText={isUnavailable ? "Close request" : "Apply"}
        okButtonProps={isUnavailable ? { danger: true, disabled: !note.trim() }
          : outcome === "attention" ? { disabled: !askOk } : undefined}
        confirmLoading={busy}
        onOk={apply}
        onCancel={() => setOpen(false)}
        destroyOnClose
      >
        <p style={{ marginBottom: 8 }}>
          {outcome === "success"
            ? "Marks this policy's work complete and closes its request."
            : isUnavailable
              ? "Closes this policy's breakdown request as unable to obtain. The office is emailed and finds it "
                + "under “Unable to obtain breakdown” on their Ready tab. Say why — the note is what they read. "
                + "It isn't billed."
              : "Tick what the office must provide, and add a message if there's more to say. If they have a "
                + "request open on this policy, it moves to the top of their Ready tab with a form for exactly "
                + "these details, and they're emailed."}
        </p>
        {outcome === "attention" ? (
          <div data-testid="details-needed-ask" style={{ marginBottom: 10 }}>
            {groups.map((group) => (
              <div key={group.key} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#6f6c80", marginBottom: 4 }}>{group.label}</div>
                <Checkbox.Group
                  value={askFields.filter((k) => group.items.some((it) => it.key === k))}
                  options={group.items.map((it) => ({ label: it.label, value: it.key }))}
                  onChange={(values) => toggleGroup(group, values)}
                />
              </div>
            ))}
            <Checkbox checked={otherOn} onChange={(e) => setOtherOn(e.target.checked)}>Other</Checkbox>
            {otherOn ? (
              <Input
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                placeholder="What else they should send or confirm"
                maxLength={500}
                aria-label="Other"
                style={{ marginTop: 6 }}
              />
            ) : null}
          </div>
        ) : null}
        <Input.TextArea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={isUnavailable
            ? "e.g. The carrier only releases breakdowns to the member — please ask the patient to request it."
            : outcome === "attention" ? "Message to the office (optional)" : "Note for this policy"}
        />
      </Modal>
    </>
  );
}
